import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { EbwModalService } from '@ebw-monorepo/modal';
import { SplitViewService, UtilsService } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { SplitViewConf } from 'libs/ng-components/utils/src/lib/models/split-view-conf';
import moment from 'moment';
import { debounceTime, filter, firstValueFrom, startWith, switchMap } from 'rxjs';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { GeoJSON } from 'ol/format';
import { IMapTable } from '../../../interfaces/mapTable.interface';
import { MapTableService } from '../../../services/map-table.service';
import { MapSearchService } from '../../controls/map-search/map-search.service';
import { CheckinAnalysisTableComponent } from './checkin-analysis-table/checkin-analysis-table.component';

export enum AttivitaType {
  RC = 1,
  LINK = 2
}

@Component({
  selector: 'ebw-monorepo-checkin-analysis',
  templateUrl: './checkin-analysis.component.html',
  styleUrls: ['./checkin-analysis.component.scss']
})
export class CheckinAnalysisComponent implements OnInit {
  public filtersForm: FormGroup;
  public typeEnum = AttivitaType;
  public autoCompletefilteredOptions;
  private _contextId = 'CheckInAnalysisTable';
  private _listening = true;
  private _selectByUrnsUrls;

  companiesList: any = [];
  formCompany: any;
  selectedCompany: any = null;

  // ------------------------------------------------------------------------------------------------

  constructor(
    private _utilsService: UtilsService,
    private _splitViewService: SplitViewService,
    private _mapTableService: MapTableService,
    private _ebwModal: EbwModalService,
    private _translateService: TranslateService,
    private _mapSearchService: MapSearchService,
    private apiUrlService: ApiUrlService
  ) {}

  // ------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._selectByUrnsUrls = `${this.apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN)}`;

    this.filtersForm = new FormGroup({
      type: new FormControl(1),
      filter: new FormControl(''),
      date_range: new FormGroup({
        start: new FormControl(null),
        end: new FormControl(null)
      })
    });

    let url = this.apiUrlService.createUrl(API_URLS.USER.COMPANY.LIST);
    this._utilsService.getRequest(url, {}).subscribe({
      next: (data) => {
        this.companiesList = data;
        if (data.length == 1) {
          this.formCompany = data[0];
        }
      },
      error: (err) => {
        console.error('[checkin-analysis] - [getCompanies] - error', err);
        this._ebwModal.openErrorModal({
          title: this._translateService.instant('common.error'),
          content: 'Errore recupero aziende'
        });
      }
    });

    this.filtersForm
      .get('filter')
      .valueChanges.pipe(
        filter((value: string) => this._listening && this.filtersForm.get('type').value === this.typeEnum.LINK),
        startWith(''),
        filter((value: string) => value.length >= 3),
        debounceTime(300),
        switchMap((val) => {
          return this._mapSearchService.getAutocompleteOpts(val, 'url:(*fw_link*)');
        })
      )
      .subscribe({
        next: (val) => {
          //INFO: filtro presente perchè probabilemente questa chiamata è errata
          // /saas/restrictedArea/search?term=seg&filter=url:(*fw_link*)
          try {
            this.autoCompletefilteredOptions = val.filter((_) => {
              const params = _.urns.split('Z');
              const collection = params[2].split('V')[0];
              return collection === 'fw_link';
            });
          } catch (error) {
            console.log(error);
          }
        },
        error: (err) => {
          console.error('[checkin-analysis] - [valueChanges] - error', err);
          this._ebwModal.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: 'Errore recupero analisi'
          });
        }
      });
  }

  onSelectCompany(event: any) {
    this.formCompany = this.companiesList.find((el) => el.id == event.value);
  }

  search() {
    let config: IMapTable = {
      url: `${this.apiUrlService.createUrl(API_URLS.CHECKIN.ANALYSIS)}`,
      title: 'map.sidebar.checkin_analysis.title',
      filters: this._mappedFilter()
    };

    const splitViewConf: SplitViewConf = {
      title: config.title,
      icon: 'table_view'
    };
    this._splitViewService.show(CheckinAnalysisTableComponent, this._contextId, null, splitViewConf);
    this._mapTableService.setMapTableConfig(config);
  }

  async onAutocompleteSelectionChanged(event: any) {
    this._listening = false;
    try {
      const response = await firstValueFrom(
        this._utilsService.postRequest(this._selectByUrnsUrls, { urns: event.option.value.urns }, false)
      );

      if (!response?.result) {
        throw new Error('No result found');
      }

      const features = new GeoJSON().readFeatures(response.result);

      if (!features.length) {
        throw new Error('No feature found');
      }

      const { name } = features[0].getProperties();

      this.filtersForm.get('filter')?.setValue(name);
    } catch (error) {
      this.filtersForm.get('filter').reset();
      this.autoCompletefilteredOptions = null;
      console.log(error);
    }

    this._listening = true;
    this.autoCompletefilteredOptions = null;
  }

  private _mappedFilter() {
    let value = this.filtersForm.getRawValue();
    let filter = {
      [value.type === this.typeEnum.RC ? 'segment_rc' : 'segment_name']: value.filter,
      dt_start: value.date_range
        ? value.date_range?.start
          ? moment(value.date_range?.start).format('YYYY-MM-DD')
          : null
        : null,
      dt_end: value.date_range
        ? value.date_range?.end
          ? moment(value.date_range?.end).format('YYYY-MM-DD')
          : null
        : null
    };

    if (this.formCompany) {
      filter.system = this.formCompany.name;
    }

    return filter;
  }

  reset() {
    this.filtersForm.reset();
    this.filtersForm.get('type').setValue(this.typeEnum.RC);
    this.selectedCompany = null;
    this.formCompany = null;
  }
}
